import React, { useState, useEffect } from "react";
import { Row, Col, Button, Form, Dropdown, Modal, Alert } from "react-bootstrap";
import { IoTimeOutline, IoEye } from "react-icons/io5";
import { FaEdit } from "react-icons/fa";
import { FcApproval } from "react-icons/fc";
import { TbCalendarCancel } from "react-icons/tb";
import { Link } from "react-router-dom";
import axios from '../../Authservices/axiosInstance';
import "./pending-leave.css";
import LeaveLayout from "../../components/LeaveLayout.jsx";

export default function PendingLeave() {
  const rowsPerPage = 5; // Rows per page for pagination
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0); // Set total entries from the backend

  // Fetch pending leaves with optional search and pagination
  useEffect(() => {
    const fetchPendingLeaves = async () => {
      try {
        const response = await axios.get(
          `https://backend.ems.uk.hiradentallab.com/api/leaves/pending?search=${searchTerm}&page=${currentPage}&limit=${rowsPerPage}`
        );
        setPendingLeaves(response.data.pendingLeaves);
        setTotalEntries(response.data.totalEntries || 0); // Fallback to 0 if totalEntries is not available
        setIsLoading(false);
      } catch (err) {
        setError("Failed to fetch pending leaves");
        setIsLoading(false);
      }
    };

    fetchPendingLeaves();
  }, [searchTerm, currentPage]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to page 1 when search changes
  };

  const updateLeaveStatus = async (leaveId, status) => {
    try {
      const role = sessionStorage.getItem("role");
      if (!role) {
        return alert("Role is not available.");
      }

      const response = await axios.put(
        `https://backend.ems.uk.hiradentallab.com/api/leaves/leave/${leaveId}/status`,
        { status },
        {
          headers: {
            role, // Send role in headers
          },
        }
      );

      setPendingLeaves((prevLeaves) =>
        prevLeaves.map((leave) =>
          leave._id === leaveId ? response.data.leave : leave
        )
      );

      setAlert({
        type: "success",
        message: "Leave status updated successfully!",
      });
    } catch (error) {
      setAlert({
        type: "danger",
        message: "There was an error updating the leave status. Please try again.",
      });
    }
  };

  // Pagination logic
  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(totalEntries / rowsPerPage); // Calculate total pages
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= Math.ceil(totalEntries / rowsPerPage)
    ) {
      setCurrentPage(pageNumber);
    }
  };

  const handleShow = (data) => {
    setModalData(data);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  return (
    <LeaveLayout>
      <div>
        <h1 className="pending-leave-header">
          <IoTimeOutline className="pending-leave-icon" />
          Pending Leaves
        </h1>
        <hr />

        {/* Show Alert if there is one */}
        {alert && (
          <Alert
            variant={alert.type}
            onClose={() => setAlert(null)}
            dismissible
          >
            {alert.message}
          </Alert>
        )}

        <div className="pending-leave-content">
          {isLoading ? (
            <p>Loading...</p>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : (
            <div>
              <div className="filter-container">
                <Col xs={12} md={6} className="filter-left">
                  <Dropdown className="d-inline mx-2" autoClose="inside">
                    <Dropdown.Toggle id="dropdown-autoclose-inside">
                      Show Entries
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="custom-dropdown-menu">
                      <Dropdown.Item href="#">5</Dropdown.Item>
                      <Dropdown.Item href="#">10</Dropdown.Item>
                      <Dropdown.Item href="#">25</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
                <Col xs={12} md={6} className="filter-right">
                  <Form>
                    <Form.Control
                      type="search"
                      placeholder="Search"
                      aria-label="Search"
                      value={searchTerm}
                      onChange={handleSearchChange} // Update search query dynamically
                    />
                  </Form>
                </Col>
              </div>

              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="table-header">
                    <tr className="total-leave-table-header">
                      <th>SR</th>
                      <th>Name</th>
                      <th>Leave Type</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Applied Date</th>
                      <th>Status</th>
                      <th>HR Approval</th>
                      <th>Admin Approval</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingLeaves.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="text-center">
                          No pending leaves available
                        </td>
                      </tr>
                    ) : (
                      pendingLeaves.map((leave, index) => (
                        <tr key={leave._id}>
                          <td>{index + 1}</td>
                          <td>{leave.name}</td>
                          <td>{leave.leaveType}</td>
                          <td>
                            {new Date(leave.startDate).toLocaleDateString()}
                          </td>
                          <td>
                            {new Date(leave.endDate).toLocaleDateString()}
                          </td>
                          <td>
                            {new Date(leave.appliedDate).toLocaleDateString()}
                          </td>
                          <td>{leave.status}</td>
                          <td>{leave.hrAction}</td>
                          <td>{leave.adminAction}</td>
                          <td>
                            <Button
                              variant="success"
                              size="sm"
                              style={{ marginRight: "10px" }}
                              onClick={() =>
                                updateLeaveStatus(leave._id, "Approved")
                              }
                              disabled={leave.status !== "Pending"}
                            >
                              <FcApproval />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() =>
                                updateLeaveStatus(leave._id, "Rejected")
                              }
                              disabled={leave.status !== "Pending"}
                            >
                              <TbCalendarCancel />
                            </Button>
                          </td>
                          <td>
                            <div className="action-button">
                              <Link to="/edit-leave">
                                <Button variant="warning" size="sm">
                                  <FaEdit />
                                </Button>
                              </Link>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleShow(leave)}
                              >
                                <IoEye />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination-row">
                <div className="entries-info">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                  {Math.min(currentPage * rowsPerPage, totalEntries)} of{" "}
                  {totalEntries} entries
                </div>
                <nav>
                  <ul className="pagination no-gap">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                      >
                        Previous
                      </a>
                    </li>
                    {pageRange().map((pageNumber) => (
                      <li
                        key={pageNumber}
                        className={`page-item ${
                          currentPage === pageNumber ? "active" : ""
                        }`}
                      >
                        <a
                          href="/#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(pageNumber);
                          }}
                        >
                          {pageNumber}
                        </a>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === Math.ceil(totalEntries / rowsPerPage)
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>

        {/* Modal for leave details */}
        <Modal show={showModal} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Leave Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Name: {modalData.name}</p>
            <p>Leave Type: {modalData.leaveType}</p>
            <p>Start Date: {modalData.startDate}</p>
            <p>End Date: {modalData.endDate}</p>
            <p>Applied Date: {modalData.appliedDate}</p>
            <p>Status: {modalData.status}</p>
            <p>HR Action: {modalData.hrAction}</p>
            <p>Admin Action: {modalData.adminAction}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </LeaveLayout>
  );
}
